import moment from "moment";
import { AxiosError } from "axios";
import { ICriptoExchangeRateResponse, ICurrencyExchangeRateResponse, IFundDetails, IStockDetails } from "../models/exchangeRate";
import { IAuthHttp } from "../hooks/useAuthHttp";
import { MissingQuoteInfo, parseMissingQuoteBody } from "../commons/missing-quote";

export type ExchangeRateSuccess = { id: string; cotation: number };
export type ExchangeRateResult = ExchangeRateSuccess | MissingQuoteInfo;

export interface IExchangeCommand {
    executeAsync: () => Promise<ExchangeRateResult | null>;
}

const missingQuoteFromError = (error: unknown): MissingQuoteInfo | null => {
    const axiosError = error as AxiosError;
    return parseMissingQuoteBody(axiosError.response?.data);
};

export const CriptoExchangeRateCommand = (
    code: string, 
    exchangeDate: Date,
    authHttp: IAuthHttp
): IExchangeCommand => {
    const executeAsync = async () => {
        const response = await authHttp.doGet<ICriptoExchangeRateResponse>(`/cripto/${code}?date=${moment(exchangeDate).format("YYYY-MM-DD")}`);

        if (!response) {
            return null;
        }

        return {
            id: response.cripto_code_to,
            cotation: response.exchange_rate!
        };
    }

    return {
        executeAsync
    };
}

export const CurrencyExchangeRateCommand = (
    code: string, 
    exchangeDate: Date,
    authHttp: IAuthHttp
): IExchangeCommand => {
    const executeAsync = async () => {
        const response = await authHttp.doGet<ICurrencyExchangeRateResponse>(`/currency/${code}?date=${moment(exchangeDate).format("YYYY-MM-DD")}`);

        if (!response) {
            return null;
        }

        return {
            id: response.currency_code_to,
            cotation: response.exchange_buy_rate
        };
    }

    return {
        executeAsync
    };
}

export const FundsExchangeRateCommand = (
    code: string, 
    exchangeDate: Date,
    authHttp: IAuthHttp,
    fundType?: "B3" | "CVM"
): IExchangeCommand => {
    const executeAsync = async () => {
        try {
            const response = await authHttp.doGet<IFundDetails>(`/inv_fund/${code}?date=${moment(exchangeDate).format("YYYY-MM-DD")}&fund_type=${fundType ?? "B3"}`);

            if (!response) {
                return null;
            }

            return {
                id: response.id,
                cotation: response.last_transaction_price
            };
        } catch (error) {
            const missing = missingQuoteFromError(error);
            if (missing) {
                return missing;
            }
            throw error;
        }
    }

    return {
        executeAsync
    };
}

export const DebentureExchangeRateCommand = (
    code: string, 
    exchangeDate: Date,
    authHttp: IAuthHttp
): IExchangeCommand => {
    const executeAsync = async () => {
        try {
            const response = await authHttp.doGet<IStockDetails>(`/debenture/${encodeURIComponent(code)}?date=${moment(exchangeDate).format("YYYY-MM-DD")}`);

            if (!response) {
                return null;
            }

            return {
                id: response.id,
                cotation: response.last_transaction_price
            };
        } catch (error) {
            const missing = missingQuoteFromError(error);
            if (missing) {
                return missing;
            }
            throw error;
        }
    }

    return {
        executeAsync
    };
}

export const StockExchangeRateCommand = (
    code: string, 
    exchangeDate: Date,
    authHttp: IAuthHttp
): IExchangeCommand => {
    const executeAsync = async () => {
        try {
            const response = await authHttp.doGet<IStockDetails>(`/stock/${code}?date=${moment(exchangeDate).format("YYYY-MM-DD")}`);

            if (!response) {
                return null;
            }

            return {
                id: response.id,
                cotation: response.last_transaction_price
            };
        } catch (error) {
            const missing = missingQuoteFromError(error);
            if (missing) {
                return missing;
            }
            throw error;
        }
    }

    return {
        executeAsync
    };
}
